import React, { useMemo, memo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { EnergyLogRecord } from '../../../hooks/useHistoricalData';

interface EnergyBarChartProps {
  data: EnergyLogRecord[];
  loading?: boolean;
}

interface HourlyEnergy {
  hour: string;
  hourNum: number;
  selfConsumption: number;  // Solar used by load (kWh)
  export: number;           // Solar exported (kWh)
  import: number;           // Grid import (kWh)
}

// Custom tooltip - memoized outside component
const CustomTooltip = memo(({ active, payload, label }: any) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <div className="bg-slate-800 border border-slate-600 rounded-lg p-3 shadow-lg">
      <p className="text-slate-300 text-sm font-medium mb-2">{label}</p>
      {payload.map((entry: any, index: number) => (
        <p key={index} className="text-sm" style={{ color: entry.color }}>
          {entry.name}: <span className="font-semibold">{entry.value.toFixed(2)} kWh</span>
        </p>
      ))}
      <p className="text-slate-400 text-xs mt-2 pt-2 border-t border-slate-700">
        Total Solar: {((payload[0]?.value || 0) + (payload[1]?.value || 0)).toFixed(2)} kWh
      </p>
    </div>
  );
});

export const EnergyBarChart = memo(function EnergyBarChart({ data, loading }: EnergyBarChartProps) {
  // Aggregate data by hour
  const hourlyData = useMemo(() => {
    if (data.length < 2) return [];

    // Group records by hour
    const hourlyGroups: { [hour: number]: EnergyLogRecord[] } = {};

    data.forEach((record) => {
      const date = new Date(record.timestamp);
      const hour = date.getHours();
      if (!hourlyGroups[hour]) {
        hourlyGroups[hour] = [];
      }
      hourlyGroups[hour].push(record);
    });

    // Calculate energy for each hour
    const result: HourlyEnergy[] = [];

    Object.keys(hourlyGroups)
      .map(Number)
      .sort((a, b) => a - b)
      .forEach((hour) => {
        const records = hourlyGroups[hour];
        if (records.length < 2) return;

        let selfConsumption = 0;
        let exportEnergy = 0;
        let importEnergy = 0;

        // Integrate power readings to get energy
        for (let i = 1; i < records.length; i++) {
          const prev = records[i - 1];
          const curr = records[i];
          const hoursElapsed = (curr.timestamp - prev.timestamp) / (1000 * 60 * 60);

          // Average power over interval, convert to kWh
          selfConsumption += ((prev.solarToLoad + curr.solarToLoad) / 2) * hoursElapsed / 1000;
          exportEnergy += ((prev.solarToGrid + curr.solarToGrid) / 2) * hoursElapsed / 1000;
          importEnergy += ((prev.gridToLoad + curr.gridToLoad) / 2) * hoursElapsed / 1000;
        }

        result.push({
          hour: `${hour.toString().padStart(2, '0')}:00`,
          hourNum: hour,
          selfConsumption: Math.round(selfConsumption * 100) / 100,
          export: Math.round(exportEnergy * 100) / 100,
          import: Math.round(importEnergy * 100) / 100,
        });
      });

    return result;
  }, [data]);

  if (loading) {
    return (
      <div className="bg-card-bg border border-slate-700 rounded-lg p-4 h-64 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-solar border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-slate-400 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (hourlyData.length === 0) {
    return (
      <div className="bg-card-bg border border-slate-700 rounded-lg p-4 h-64 flex items-center justify-center">
        <div className="text-center">
          <svg className="w-10 h-10 text-slate-600 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <p className="text-slate-400 text-sm">No energy data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card-bg border border-slate-700 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wide">
          Hourly Energy
        </h3>
      </div>

      <div className="h-52">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={hourlyData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis
              dataKey="hour"
              stroke="#6b7280"
              tick={{ fontSize: 10 }}
              interval={2}
            />
            <YAxis
              stroke="#6b7280"
              tick={{ fontSize: 10 }}
              tickFormatter={(v) => `${v}`}
              label={{
                value: 'kWh',
                angle: -90,
                position: 'insideLeft',
                style: { textAnchor: 'middle', fill: '#6b7280', fontSize: 10 },
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ paddingTop: '5px' }}
              formatter={(value) => <span className="text-slate-300 text-xs">{value}</span>}
            />

            {/* Stacked: Self-consumption (green) + Export (blue) */}
            <Bar
              dataKey="selfConsumption"
              name="Self-Consumption"
              stackId="solar"
              fill="#22c55e"
              radius={[0, 0, 0, 0]}
              isAnimationActive={false}
            />
            <Bar
              dataKey="export"
              name="Export"
              stackId="solar"
              fill="#3b82f6"
              radius={[2, 2, 0, 0]}
              isAnimationActive={false}
            />

            {/* Import shown separately (red) */}
            <Bar
              dataKey="import"
              name="Import"
              fill="#ef4444"
              radius={[2, 2, 0, 0]}
              isAnimationActive={false}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
});

export default EnergyBarChart;
